import {
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Button,
} from '@mui/material';
import type { ResourceIdentifiers } from '@repo/api-gw-sdk';
import { useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import OptionMenu from '@/components/option-menu';

import { ResourceIdentifierItem } from './resourceIdentifierItem';

export const ResourcesSelector = ({
  label,
  description,
  allResources,
  resources,
  addResource,
  removeResource,
}: {
  label: string;
  description: string;
  allResources: ResourceIdentifiers[];
  resources: string[];
  addResource: (providerResourceId: string) => void;
  removeResource: (providerResourceId: string) => void;
}) => {
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const onAddPressed = () => {
    if (!resources.includes(filter) && filter.length > 8) {
      addResource(filter);
      setFilter('');
    }
  };

  const options = allResources.filter(
    (resource) => !resources.includes(resource.providerId)
  );

  return (
    <Accordion expanded={isExpanded}>
      <AccordionSummary onClick={() => setIsExpanded(!isExpanded)}>
        <Stack
          width='100%'
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='body1'>{label}</Typography>
          {!isExpanded && (
            <Typography
              sx={{ fontWeight: '400 !important' }}
              variant='subtitle1'
            >
              {resources.length}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '12px 0px 24px 36px' }}>
        <Stack marginY='12px 24px' gap='24px'>
          <Stack
            direction='row'
            gap='12px'
            alignItems='center'
            justifyContent='space-between'
          >
            <Autocomplete
              sx={{ flexGrow: 1 }}
              freeSolo
              open={isOpen}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              options={options}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.providerId
              }
              renderOption={(props, option) => (
                <li {...props} key={option.providerId}>
                  <ResourceIdentifierItem
                    providerId={option.providerId}
                    name={option.name}
                  />
                </li>
              )}
              inputValue={filter}
              onInputChange={(event, newInputValue) => {
                // for when reset the filter
                if (event) {
                  setFilter(newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '32px',
                    },
                    '& .MuiInputBase-input': {
                      padding: 0,
                      height: 'auto',
                    },
                  }}
                  placeholder='Search or type resource ID...'
                  InputProps={{
                    ...params.InputProps,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const matchingOptions = options.filter((option) =>
                        option.providerId
                          .toLowerCase()
                          .includes(filter.toLowerCase())
                      );

                      if (!isOpen || matchingOptions.length === 0) {
                        onAddPressed();
                      } else {
                        // required another Enter to add the resource list
                        setIsOpen(false);
                      }
                    }
                  }}
                />
              )}
            />
            <Button
              className='!min-w-[20px] p-[6px]'
              size='small'
              variant='contained'
              disabled={!filter || filter.length < 9}
              onClick={onAddPressed}
            >
              <i className='material-symbols-add-rounded w-[20px] h-[20px]' />
            </Button>
          </Stack>
          <Typography variant='subtitle1'>{description}</Typography>
          <Stack gap='12px'>
            {resources
              .map((r) =>
                allResources.find((resource) => resource.providerId === r)
              )
              .filter((resource) => resource !== undefined)
              .map((resource) => {
                return (
                  <Stack
                    key={resource.providerId}
                    direction='row'
                    gap='12px'
                    alignItems='center'
                    justifyContent='space-between'
                  >
                    <ResourceIdentifierItem
                      providerId={resource.providerId}
                      name={resource.name}
                    />
                    <OptionMenu
                      iconClassName='text-textPrimary'
                      options={[
                        {
                          text: 'Remove',
                          menuItemProps: {
                            onClick: () => removeResource(resource.providerId),
                          },
                          testId: 'remove-resource',
                        },
                      ]}
                    />
                  </Stack>
                );
              })}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
